import { ChangeEvent, useState } from 'react'

import {
  Button,
  Chip,
  Divider,
  Modal,
  ModalProvider,
  MultiValueTextField,
  SecondaryButton,
  ValidationType,
  typography,
} from '@joor/design-system'
import styled from 'styled-components'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'
import { fromGlobalId } from 'utils/transformations/graphql'

import usePortal from 'hooks/usePortal'
import { useRenderToasters } from 'hooks/useRenderToasters'

import {
  AdvancedSettings,
  AdvancedSettingsValueType,
  Setting,
} from './AdvancedSettings/AdvancedSettings'
import { MessageTextArea } from './MessageTextArea/MessageTextArea'
import { useOrderContactsData } from './ordersShareModal.hooks'
import {
  isRequestSuccessGuard,
  sendShareEmails,
} from './ordersShareModal.mutations'
import { ContactList } from 'features/Orders/components'

type Props = {
  title?: string
  textAreaPlaceholder?: string
  textAreaTitle?: string
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  orderIds: string[]
  showTags?: boolean
}

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 24px;
  gap: 24px;
`

const StyledActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  order: 3;
  padding: 24px 24px 8px 24px;
  margin-top: 10px;
  border-top: 1px solid var(--color-primary-200);

  > span {
    ${typography.alpha.primaryActive.body3}
    color: var(--color-primary-400)
  }
  > div {
    display: flex;
    gap: 16px;
  }
`

const StyledDivider = styled(Divider)`
  padding: 0 8px;
`
const ScrollableContentWrapper = styled.div`
  overflow-y: scroll;
  max-height: 260px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 0px 24px;
`

const ADVANCED_OPTIONS_LABELS: Record<string, string> = {
  [Setting.MergeOrders]: 'Merge',
  [Setting.ZipPdfs]: 'ZIP',
  [Setting.ExcludeTerms]: 'Exclude Terms',
  [Setting.ExcludeImages]: 'Exclude Images',
}

const getCheckedOptionsText = (
  advancedSettings: AdvancedSettingsValueType,
): string[] => {
  let checked: string[] = []
  Object.entries(advancedSettings).forEach((setting) => {
    if (setting[1]) checked.push(ADVANCED_OPTIONS_LABELS[setting[0]])
  })
  return checked
}

const OrdersShareModal = ({
  title = 'Share Orders',
  isOpen,
  setOpen,
  textAreaPlaceholder,
  textAreaTitle,
  orderIds,
}: Props) => {
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [emailChips, setEmailChips] = useState<Chip[]>([])
  const [advancedSettings, setAdvancedSettings] =
    useState<AdvancedSettingsValueType>({
      [Setting.MergeOrders]: false,
      [Setting.ZipPdfs]: false,
      [Setting.ExcludeTerms]: false,
      [Setting.ExcludeImages]: false,
    })

  const handleAddEmail = (newChip: Chip) => {
    if (emailChips.some(({ value }) => value === newChip.value)) return
    setEmailChips([...emailChips, newChip])
    trackEvent('Order_Review_Share_Modal_(Add_Email_Button)')
  }

  const handleRemoveEmail = (chipToDelete: Chip) => {
    setEmailChips(
      emailChips.filter((email) => email.value !== chipToDelete.value),
    )
  }

  const { buyers } = useOrderContactsData(orderIds)

  const { trackEvent } = useUserAnalytics()

  const collaboratorCount = (buyers?.length || 0) + emailChips.length

  const Portal = usePortal()
  const { renderSuccessToast, renderErrorToast } = useRenderToasters()

  const handleChangeSettings = (event: ChangeEvent<HTMLInputElement>): void => {
    const { id, checked } = event.target
    setAdvancedSettings({ ...advancedSettings, [id]: checked })
  }

  const handleSubmit = async (): Promise<void> => {
    try {
      setLoading(true)
      const res = await sendShareEmails({
        message_body: message,
        custom_message: Boolean(message),
        custom_recipient: Boolean(emailChips?.length),
        message_subject: '',
        additional_recipients: emailChips?.length
          ? emailChips?.map((chip) => chip.value).join(',')
          : '',
        order_ids: orderIds?.map((orderId) => fromGlobalId(orderId)?.id),
        exclude_terms: advancedSettings?.excludeTerms,
        single_pdf: advancedSettings?.mergeOrders,
        zip: advancedSettings?.zipPdfs,
        exclude_images: advancedSettings?.excludeImages,
      })
      if (isRequestSuccessGuard(res?.data)) {
        renderSuccessToast({ description: 'Order shared successfully' })
      }
      setLoading(false)
    } catch (err) {
      renderErrorToast({
        description:
          'There was an error while trying to share the order. Please try again.',
      })
      setLoading(false)
    }
  }

  return (
    <Portal>
      <ModalProvider>
        <Modal
          isOpen={isOpen}
          onClose={(): void => {
            setOpen(false)
            trackEvent('Order_Review_Share_Button_(Close)')
          }}
          title={{
            text: title,
          }}
        >
          <StyledDivider variant="light" />
          <ContentWrapper>
            <ContactList salesRepresentatives={[]} buyers={buyers} />
            <MultiValueTextField
              value={emailChips}
              placeholder={emailChips.length ? '' : 'Add additional emails'}
              onAddChip={handleAddEmail}
              onCloseChip={handleRemoveEmail}
              validation={ValidationType.Email}
              errorMessage="Please enter a valid email address."
            />
          </ContentWrapper>
          <ScrollableContentWrapper>
            <MessageTextArea
              value={message}
              onChange={setMessage}
              placeholder={textAreaPlaceholder}
              title={textAreaTitle}
            />
            <AdvancedSettings
              value={advancedSettings}
              onChange={handleChangeSettings}
            />
          </ScrollableContentWrapper>
          <StyledActions>
            <span>
              {!emailChips.length
                ? 'Buyer will be notified.'
                : `${collaboratorCount} People will be notified.`}
            </span>
            <div>
              <SecondaryButton
                onClick={(): void => {
                  setOpen(false)
                  trackEvent('Order_Review_Share_Button_(Close)')
                }}
              >
                Cancel
              </SecondaryButton>
              <Button
                onClick={async () => {
                  await handleSubmit()
                  setOpen(false)
                  trackEvent('Order_Review_Share_Modal_(Submit)', {
                    Checks: getCheckedOptionsText(advancedSettings).join(', '),
                  })
                }}
                loading={loading}
              >
                Send
              </Button>
            </div>
          </StyledActions>
        </Modal>
      </ModalProvider>
    </Portal>
  )
}

export default OrdersShareModal
